import { useMemo, useState } from "react";
import type { Task, PlanEvent, Habit, Category, CategoryFilter } from "../../../../shared/types";
import { toDateString } from "../../../../shared/types/date-helpers";
import { useCategories } from "../../../../shared/stores/category-store";
import { ActivityItemRow, type ActivityItem } from "./ActivityComponents";

interface ActivityLogProps {
  readonly tasks?: Task[];
  readonly events?: PlanEvent[];
  readonly habits?: Habit[];
}

const INITIAL_VISIBLE = 5;

export function ActivityLog({
  tasks = [],
  events = [],
  habits = [],
}: ActivityLogProps) {
  const { categories, labels } = useCategories();
  const [filter, setFilter] = useState<CategoryFilter>("all");
  const [expanded, setExpanded] = useState(false);

  const activities = useMemo(() => {
    const items: ActivityItem[] = [];

    tasks.forEach((task) => {
      if (task.status === "done") {
        items.push({
          type: "task_completed",
          title: task.title,
          category: task.category,
          timestamp: task.createdAt,
          date: toDateString(new Date(task.createdAt)),
        });
      } else {
        items.push({
          type: "task_created",
          title: task.title,
          category: task.category,
          timestamp: task.createdAt,
          date: toDateString(new Date(task.createdAt)),
        });
      }
    });

    events.forEach((event) => {
      items.push({
        type: "event_created",
        title: event.title,
        category: event.category,
        timestamp: event.createdAt,
        date: toDateString(new Date(event.createdAt)),
      });
    });

    habits.forEach((habit) => {
      habit.completedDates.forEach((dateStr) => {
        const d = new Date(dateStr + "T00:00:00");
        items.push({
          type: "habit_checked",
          title: habit.title,
          category: habit.category,
          timestamp: d.getTime(),
          date: dateStr,
        });
      });
    });

    return items
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 50)
      .reduce(
        (grouped, item) => {
          const lastGroup = grouped[grouped.length - 1];
          if (lastGroup && lastGroup.date === item.date) {
            lastGroup.items.push(item);
          } else {
            grouped.push({ date: item.date, items: [item] });
          }
          return grouped;
        },
        [] as Array<{ date: string; items: ActivityItem[] }>,
      );
  }, [tasks, events, habits]);

  const filtered = useMemo(() => {
    if (filter === "all") return activities;
    return activities
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => item.category === filter),
      }))
      .filter((group) => group.items.length > 0);
  }, [activities, filter]);

  const totalItems = filtered.reduce((sum, g) => sum + g.items.length, 0);
  const needsExpand = totalItems > INITIAL_VISIBLE;

  const visible = useMemo(() => {
    if (expanded) return filtered;
    let remaining = INITIAL_VISIBLE;
    const result: typeof filtered = [];
    for (const group of filtered) {
      if (remaining <= 0) break;
      if (group.items.length <= remaining) {
        result.push(group);
        remaining -= group.items.length;
      } else {
        result.push({ ...group, items: group.items.slice(0, remaining) });
        remaining = 0;
      }
    }
    return result;
  }, [filtered, expanded]);

  if (activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <p className="text-ink-muted text-sm">Aucune activité récente</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-4">
      <div className="flex items-center justify-between px-1">
        <h3 className="text-xs font-semibold text-ink-faint uppercase">
          Activité récente
        </h3>
        <div className="flex flex-wrap gap-1">
          <button
            type="button"
            onClick={() => setFilter("all")}
            className={`px-2 py-1 text-[10px] font-medium rounded-md transition-colors ${
              filter === "all"
                ? "bg-accent text-white"
                : "text-ink-muted hover:text-ink bg-surface-alt"
            }`}
          >
            Tout
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setFilter(cat as Category)}
              className={`px-2 py-1 text-[10px] font-medium rounded-md transition-colors ${
                filter === cat
                  ? "bg-accent text-white"
                  : "text-ink-muted hover:text-ink bg-surface-alt"
              }`}
            >
              {labels[cat]}
            </button>
          ))}
        </div>
      </div>
      {visible.map((group) => (
        <div key={group.date}>
          <h4 className="text-xs font-semibold text-ink-faint mb-2 px-1">
            {new Date(group.date + "T00:00:00").toLocaleDateString("fr-FR", {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}
          </h4>
          <div className="space-y-2">
            {group.items.map((item, idx) => (
              <ActivityItemRow
                key={`${item.date}_${item.type}_${idx}`}
                item={item}
                index={idx}
              />
            ))}
          </div>
        </div>
      ))}
      {needsExpand && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full py-2.5 text-xs font-medium text-accent hover:text-accent-hover
            bg-surface-alt hover:bg-surface border border-border rounded-lg transition-colors"
        >
          {expanded ? "Voir moins" : `Voir tout (${totalItems})`}
        </button>
      )}
    </div>
  );
}
